import type { BrandConfigInput } from "@cfg/brand-sdk";
import { attribution, contactEmail, operator, privacy, terms } from "./legal.ts";
import { esMX } from "./locales/es-MX.ts";

/**
 * BrickBrackets, dark edition of the brand kit: an Ink (#17202E) bench with
 * lighter ink panels and hairline borders, Stud Orange (#FF5A1F) as the one
 * accent the 3D model shares, Stud Yellow (#FFB81C) only for print-check
 * warnings, Paper (#F7F2E8) for text. Poppins for words, JetBrains Mono for
 * every millimetre.
 * The palette is dark by design, so there are no light/dark variants; the
 * overrides set `color-scheme: dark` so native controls follow.
 * Serializable: this object becomes brand.json at deploy.
 */
export const config: BrandConfigInput = {
  id: "brickbrackets",
  name: "BrickBrackets",
  tagline: "Parametric display brackets for LEGO® sets.",
  domain: "brickbrackets.io",
  locale: "en",
  /** English is the default; visitors whose browser prefers Spanish get es-MX (switcher in the header). */
  locales: ["en", "es-MX"],
  /** Spanish text lives in ./locales/es-MX.ts so translators edit one file. */
  i18n: { "es-MX": esMX },
  scene: "workshop",
  packs: ["brickbrackets@^1"],
  catalog: { include: ["brickbrackets.*"] },
  features: { betaChannel: false, fitTags: true, publicGallery: true, exportRequiresSignIn: true },
  fitTags: {
    label: "LEGO® set",
    refPattern: "^\\d{3,7}(-\\d{1,2})?$",
    refPlaceholder: "e.g. 42115-1",
    namePlaceholder: "Set name (optional)",
    /** "42115" means the first release, "42115-1". */
    refSuffix: "-1",
    /** Set numbers and names are searched on Rebrickable through the API (key in Secrets Manager "cfg/rebrickable"). */
    lookup: "rebrickable",
    /** Rebrickable's set pictures, for suggestions shown without a lookup (signed out). */
    imageUrl: "https://cdn.rebrickable.com/media/sets/{ref}.jpg",
    /** Typed out by the home page's set search while idle. */
    examples: ["42115-1", "Lamborghini Sián FKP 37", "75192", "Millennium Falcon", "10497-1", "Galaxy Explorer", "42143", "Ferrari Daytona SP3", "10294-1", "Titanic"],
    disclaimer: "LEGO® is a trademark of the LEGO Group, which does not sponsor, authorize or endorse this site.",
  },
  /** Footer links (Privacy, Terms, Contact) and the Rebrickable credit; texts in ./legal.ts. */
  legal: { operator, contactEmail, privacy, terms, attribution },
  meta: {
    description: "Parametric display brackets for LEGO® sets. Set the dimensions, check the print, download the STL. Generated in your browser.",
    icons: { ico: "/favicon.ico", svg: "/icon.svg", appleTouch: "/apple-touch-icon.png" },
    manifest: "/site.webmanifest",
    ogImage: { url: "/og-image.png", width: 1200, height: 630, alt: "BrickBrackets.io" },
  },
  copy: {
    "nav.catalog": "Brackets",
    "nav.mine": "My brackets",
    "catalog.title": "Pick a bracket",
    "catalog.empty": "No brackets published yet.",
    "configurator.params": "Dimensions",
    "configurator.warnings": "Print check",
    "mine.title": "My brackets",
    "mine.empty": "Nothing saved yet. Configure a bracket and press Save.",
    "community.title": "Community brackets",
    "community.searchLabel": "Set number, name or person",
    "community.emptyHint": "Tag the LEGO® set a bracket fits from the Dimensions tab to list it here.",
    "family.label": "Mount",
    "mine.favoritesEmpty": "No favorites yet. Use ☆ Favorite on a community bracket.",
    "community.favorited": "Added to Favorites in My brackets.",
    "community.addFitSubmit": "Add set",
    "community.restore": "Back to my dimensions",
    "community.locked": "A community bracket is on the model, so the dimensions are locked.",
    "community.setsLabel": "LEGO® sets it fits",
    "community.onModelLabel": "Community bracket on the model",
    "community.sets.other": "{count} sets",
    "community.sets.one": "1 set",
    "configurator.savedProfile": "Saved to My brackets.",
    "configurator.saveHeading": "My bracket",
    "mine.taggedEmpty": "No sets yet. Tag the set a bracket fits from its Dimensions tab.",
    "mine.tagged": "Your sets",
    "community.alreadyTagged": "These dimensions are already tagged for {refs}.",
    "community.tagHint": "Tell other fans which set these dimensions fit. Your name shows next to the set; you can remove it any time.",
    "configurator.new": "New bracket",
    "configurator.newHint": "Start a new bracket from these dimensions. The saved one stays as it is.",
    "configurator.startedNew": "Started a new bracket from these dimensions.",
    "community.addFit": "Add a LEGO® set it fits",
    "community.addFitPrinted": "I printed it for this set",
    "fits.hint": "Type a set number or name and pick the set, or type its number (42115 or 42115-1). Set data from Rebrickable.",
    "share.missingBody": "Ask for a new link, or start from a bracket.",
    "share.browse": "Browse brackets",
    "fits.printed": "I've printed this and it fits my set",
    "home.findLabel": "Find brackets for your LEGO® set",
    "home.findPlaceholder": "Set number or name",
    "home.resultsTitle": "Brackets tagged for {ref}",
    "home.entries.one": "1 bracket tagged for this set",
    "home.entries.other": "{count} brackets tagged for this set",
    "home.noneBody": "Pick a bracket below, size it to your set and tag it with {ref} from the Dimensions tab, so the next fan finds it here.",
    "home.allTitle": "All brackets",
    "home.step1": "Find your set",
    "home.step2": "Pick a bracket people printed",
    "home.proof": "{count} sets already have brackets tagged by the community",
    "community.appliedLive": "Community bracket on the model.",
    "community.removeLast": "{ref} is this bracket's only set: removing your tag removes the bracket from the community, prints included.",
    "community.adopt": "Start from this bracket",
    "configurator.openError": "Could not open this bracket",
    "configurator.notFound": "Bracket not found",
    "home.noMatch": "No tagged set matches “{query}”. Type the set number, like 10294, or size a bracket to your set below and be the first to tag it.",
    "home.noMatchCustomize": "Customize your own ↓",
    "home.invalid": "We couldn't find that set. Try its number, like 42115, or customize your own bracket below.",
    "fits.invalid": "That doesn't look like a set number. Try one like 42115.",
    "community.fitFilter": "Only brackets tagged for",
    "next.printedTag": "I printed it: tag my LEGO® set",
    "next.printedPick": "Which set did you print it for?",
    "next.save": "Save it to My brackets, to tweak it after a test print",
    "community.fitNone": "No bracket of this mount is tagged for {ref} yet.",
  },
  theme: {
    // WCAG contrast on bg/surface/raised/panel:
    // text 14.7/13.8/12.6/14.0, textMuted 8.0/7.6/6.9/7.6, accent-text (#FF7A47) 6.3/6.0/5.4/6.0,
    // warn 9.5/8.9/8.1/9.0, danger 6.0/5.7/5.2/5.7, success 8.6/8.1/7.4/8.2; Ink on the orange button 5.3;
    // input borders (#6B768A) 3.4 on panel, 3.9 on field.
    colors: {
      bg: "#17202E",
      surface: "#1B2535",
      surfaceRaised: "#212C3E",
      text: "#F7F2E8",
      textMuted: "#AEB6C4",
      accent: "#FF5A1F",
      accentContrast: "#17202E",
      accentSoft: "rgba(255,90,31,0.16)",
      border: "#2B3546",
      borderStrong: "#3A4458",
      danger: "#FF6B86",
      success: "#43D394",
      model: "#FF5A1F",
      stage: "#141C29",
    },
    fonts: {
      display: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      body: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    webFonts: ["Poppins:wght@400;500;600;700", "JetBrains Mono:wght@400;500"],
    radius: { sm: "8px", md: "12px", lg: "18px", pill: "999px" },
    spacing: { unit: 4 },
    type: { baseSize: "14px", ratio: 1.2, labelTracking: "0.02em" },
    shadows: {
      sm: "0 1px 2px rgba(5,9,16,0.45)",
      md: "0 8px 24px -8px rgba(5,9,16,0.6), 0 2px 6px rgba(5,9,16,0.35)",
      lg: "0 24px 60px -16px rgba(5,9,16,0.75), 0 4px 12px rgba(5,9,16,0.4)",
    },
    extras: {
      glass: "rgba(23,32,46,0.74)",
      hairline: "rgba(221,226,234,0.08)",
      highlight: "rgba(221,226,234,0.05)",
      grid: "rgba(221,226,234,0.14)",
      glow: "rgba(255,90,31,0.35)",
      ease: "cubic-bezier(0.2, 0.8, 0.2, 1)",
      // Orange as text (links, active labels): the kit's lighter orange, readable on every ink surface.
      "accent-text": "#FF7A47",
      // Stud Yellow, the secondary accent: print-check warnings and clamp hints only.
      warn: "#FFB81C",
    },
  },
};
